import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, ChevronRight, ListTodo, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgFilter } from "@/scripts/OrgFilterContext";
import { useDmDrawer } from "@/scripts/DmDrawerContext";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  getNotificationPath,
  getNotificationTitle,
  getNotificationBody,
  shouldOpenDmDrawer,
} from "@/lib/notificationRoutes";
import { listDmConversations, listTeamChannels } from "@/lib/dmApi";
import {
  boardHrefForCard,
  permalinkForPost,
  withReturnQuery,
} from "@/lib/permalinks";
import { cn } from "@/lib/utils";
import { authUserIdFromToken } from "@/lib/tokenUtils";
import {
  parentTitleOf,
  storyPointsOf,
  workKindLabel,
} from "@/lib/workKind";
import CardInspectOverlay from "./CardInspectOverlay";
import CardEditDialog from "./CardEditDialog";
import LinkedParentDialog from "./LinkedParentDialog";
import MyWorkWeekStrip from "./MyWorkWeekStrip";
import TeamRoadmap from "./TeamRoadmap";
import PersonalTasksSection from "./PersonalTasksSection";

const ATTENTION_TYPES =
  "post.critical,work.mention,work.assignee,team.handoff,channel.here";
const ATTENTION_LIMIT = 5;
const MY_WORK_PAGE_SIZE = 50;

const UPCOMING_BUCKETS = [
  {
    key: "overdue",
    label: "Overdue",
    title: "Due before today — still open and assigned to you (or a handoff)",
  },
  {
    key: "today",
    label: "Today",
    title: "Due today",
  },
  {
    key: "this_week",
    label: "This week",
    title: "Due after today through Sunday (week starts Monday)",
  },
  {
    key: "later",
    label: "Later",
    title: "Due after this Sunday",
  },
  {
    key: "no_date",
    label: "No date",
    title: "Open work with no due date set",
  },
];

const EMPTY_BUCKETS = {
  overdue: [],
  today: [],
  this_week: [],
  later: [],
  no_date: [],
};

const EMPTY_INSPECT_TEAM = {
  teamId: null,
  members: [],
  memberIds: new Set(),
  lanes: [],
  sprints: [],
  defaultChannel: null,
  isMember: false,
};

function mapTeamMembers(membersData) {
  if (!Array.isArray(membersData)) return [];
  return membersData.map((item) => {
    if (item?.user) {
      return {
        ...item.user,
        role: item.role,
        overrides: item.overrides || [],
        title: item.title,
        org_email: item.org_email || null,
        member_avatar_url:
          item.member_avatar_url || item.user?.member_avatar_url || null,
      };
    }
    return item;
  });
}

function memberIdOf(m) {
  return m?.user_id || m?.userId || m?.user?.user_id || m?.user?.userId || null;
}

function viewStorageKey(orgId) {
  return `circa.myWork.view.${orgId}`;
}

function thisSprintStorageKey(orgId) {
  return `circa.myWork.thisSprint.${orgId}`;
}

function readWorkViewPref(orgId) {
  if (!orgId || typeof window === "undefined") return "list";
  try {
    const raw = window.localStorage.getItem(viewStorageKey(orgId));
    if (raw === "upcoming" || raw === "roadmap") return raw;
    return "list";
  } catch {
    return "list";
  }
}

function writeWorkViewPref(orgId, view) {
  if (!orgId || typeof window === "undefined") return;
  try {
    const v =
      view === "upcoming" || view === "roadmap" ? view : "list";
    window.localStorage.setItem(viewStorageKey(orgId), v);
  } catch {
    /* ignore */
  }
}

function readThisSprintPref(orgId) {
  if (!orgId || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(thisSprintStorageKey(orgId)) === "1";
  } catch {
    return false;
  }
}

function writeThisSprintPref(orgId, on) {
  if (!orgId || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      thisSprintStorageKey(orgId),
      on ? "1" : "0"
    );
  } catch {
    /* ignore */
  }
}

function notificationId(n) {
  const id = n?.notification_id ?? n?.notificationId;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function postIdOf(post) {
  const id = post?.post_id ?? post?.postId;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function teamIdOf(post) {
  const id = post?.team_id ?? post?.teamId ?? post?.team?.team_id;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : 0;
}

function teamName(post) {
  return post?.team?.name || post?.team_name || "Unknown team";
}

function laneOf(post) {
  return post?.kanban_status ?? post?.kanbanStatus ?? null;
}

function sprintNameOf(post) {
  return post?.sprint_name ?? post?.sprintName ?? null;
}

function sprintIdOf(post) {
  const id = post?.sprint_id ?? post?.sprintId;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function isSprintCurrent(post) {
  return Boolean(post?.sprint_current ?? post?.sprintCurrent);
}

function priorityOf(post) {
  const p = String(post?.priority || "medium").toLowerCase();
  return p;
}

function attachmentCount(post) {
  return Array.isArray(post?.attachments) ? post.attachments.length : 0;
}

function dueDateOf(post) {
  const raw = post?.due_date ?? post?.dueDate;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return Number.isFinite(d.getTime()) ? d : null;
}

function formatDue(d) {
  try {
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

function passesThisSprintFilter(item) {
  if (item?.kind === "handoff") return true;
  return isSprintCurrent(item?.post);
}

function filterThisSprintItems(items) {
  return (items || []).filter(passesThisSprintFilter);
}

function filterThisSprintBuckets(buckets) {
  const next = { ...EMPTY_BUCKETS };
  for (const key of Object.keys(EMPTY_BUCKETS)) {
    next[key] = filterThisSprintItems(buckets?.[key] || []);
  }
  return next;
}

function flattenUpcomingBuckets(buckets) {
  return UPCOMING_BUCKETS.flatMap(({ key }) => buckets?.[key] || []);
}

function cardTitleLabel(post) {
  const raw = (post?.title && String(post.title).trim()) || "Untitled";
  return raw.replace(/^\[[^\]]+\]\s*:?\s*/, "") || "Untitled";
}

function myWorkRoadmapLabel(post) {
  const title = cardTitleLabel(post);
  const team = teamName(post);
  if (!team || team === "Unknown team") return title;
  return `${team} · ${title}`;
}

function myWorkItemKey(item) {
  const pid = postIdOf(item?.post);
  if (item?.kind === "handoff") {
    const hid =
      item.handoff?.handoff_id ?? item.handoff?.handoffId ?? pid ?? "h";
    return `handoff-${hid}-${pid}`;
  }
  return `assigned-${pid}`;
}

function fromTeamName(item) {
  return (
    item?.handoff?.from_team?.name ||
    item?.handoff?.fromTeam?.name ||
    null
  );
}

function toTeamName(item) {
  return (
    item?.handoff?.to_team?.name ||
    item?.handoff?.toTeam?.name ||
    teamName(item?.post) ||
    null
  );
}

function groupMyWorkByTeam(items) {
  const map = new Map();
  for (const item of items) {
    const post = item.post;
    const tid = teamIdOf(post);
    const name = teamName(post);
    if (!map.has(tid)) {
      map.set(tid, { teamId: tid, name, items: [] });
    }
    map.get(tid).items.push(item);
  }
  return [...map.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );
}

function MyWorkRow({ item, now, onClick, showTeam = false }) {
  const post = item.post;
  const title =
    (post?.title && String(post.title).trim()) || "Untitled card";
  const due = dueDateOf(post);
  const overdue = due && due.getTime() < now;
  const lane = laneOf(post);
  const isHandoff = item.kind === "handoff";
  const fromName = isHandoff ? fromTeamName(item) : null;
  const toName = isHandoff ? toTeamName(item) : null;
  const sprintLabel = sprintIdOf(post)
    ? sprintNameOf(post) || "Sprint"
    : "Unscheduled";
  const priority = priorityOf(post);
  const files = attachmentCount(post);

  let subtitle = null;
  if (isHandoff) {
    const route =
      fromName && toName
        ? `${fromName} → ${toName}`
        : toName || fromName || "Handoff";
    subtitle = `Handoff · ${route}`;
    const itemCount =
      Number(item?.handoff?.item_count ?? item?.handoff?.itemCount ?? 1) || 1;
    if (itemCount > 1) {
      subtitle = `${subtitle} · ${itemCount} items`;
    }
    if (sprintIdOf(post) && sprintNameOf(post)) {
      subtitle = `${subtitle} · ${sprintNameOf(post)}`;
    }
  } else {
    const parts = [];
    if (showTeam) {
      const team = teamName(post);
      if (team && team !== "Unknown team") parts.push(team);
    }
    if (lane) parts.push(lane);
    parts.push(sprintLabel);
    const parentTitle = parentTitleOf(post);
    if (parentTitle) parts.push(parentTitle);
    subtitle = parts.join(" · ");
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onClick(item)}
        className="w-full text-left rounded-md px-2 py-1.5 hover:bg-muted/60 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-foreground leading-snug min-w-0">{title}</p>
          {due ? (
            <span
              className={
                overdue
                  ? "shrink-0 text-xs font-medium text-destructive"
                  : "shrink-0 text-xs text-muted-foreground"
              }
            >
              {formatDue(due)}
            </span>
          ) : null}
        </div>
        {subtitle ? (
          <p className="text-xs text-muted-foreground truncate mt-0.5">
            {subtitle}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal">
            {workKindLabel(post)}
          </Badge>
          {storyPointsOf(post) != null ? (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal">
              {storyPointsOf(post)} pts
            </Badge>
          ) : null}
          {isHandoff ? (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 font-normal">
              Handoff
            </Badge>
          ) : null}
          {files > 0 ? (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 font-normal gap-0.5 text-muted-foreground"
            >
              <Paperclip className="h-3 w-3" />
              {files}
            </Badge>
          ) : null}
          {priority && priority !== "medium" ? (
            <Badge
              variant="outline"
              className="text-[10px] py-0 px-1.5 font-normal capitalize"
            >
              {priority}
            </Badge>
          ) : null}
        </div>
      </button>
    </li>
  );
}

/**
 * Personal agile command center: Attention + open work across teams.
 * Route: /o/:orgId/my-work
 */
const MyWorkPage = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();
  const { openConversation } = useDmDrawer();
  const overdueSectionRef = useRef(null);
  const todaySectionRef = useRef(null);
  const thisWeekSectionRef = useRef(null);

  const [orgName, setOrgName] = useState("Workspace");
  const [attentionItems, setAttentionItems] = useState([]);
  const [attentionTotal, setAttentionTotal] = useState(0);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [attentionOpen, setAttentionOpen] = useState(false);

  const [workView, setWorkView] = useState(() => readWorkViewPref(orgId));
  const [thisSprintOnly, setThisSprintOnly] = useState(() =>
    readThisSprintPref(orgId)
  );
  const [myWorkItems, setMyWorkItems] = useState([]);
  const [buckets, setBuckets] = useState(EMPTY_BUCKETS);
  const [upcomingTruncated, setUpcomingTruncated] = useState(false);
  const [myWorkTotal, setMyWorkTotal] = useState(0);
  const [summary, setSummary] = useState({
    open_assigned: 0,
    overdue: 0,
    pending_handoffs: 0,
  });
  const [myWorkLoading, setMyWorkLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inspectCard, setInspectCard] = useState(null);
  const [inspectTeam, setInspectTeam] = useState(EMPTY_INSPECT_TEAM);
  const [inspectEditOpen, setInspectEditOpen] = useState(false);
  const [remoteParentId, setRemoteParentId] = useState(null);
  const inspectTeamCacheRef = useRef(new Map());

  const returnPath = `/o/${encodeURIComponent(orgId || "")}/my-work`;

  useEffect(() => {
    if (orgId) setSelectedOrgId(orgId);
  }, [orgId, setSelectedOrgId]);

  useEffect(() => {
    setWorkView(readWorkViewPref(orgId));
    setThisSprintOnly(readThisSprintPref(orgId));
  }, [orgId]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      setOrgName("Workspace");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!cancelled) {
          setOrgName(res.data?.name || "Workspace");
        }
      } catch {
        if (!cancelled) setOrgName("Workspace");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId]);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const applySummary = (s) => {
    setSummary({
      open_assigned: Number(s?.open_assigned) || 0,
      overdue: Number(s?.overdue) || 0,
      pending_handoffs: Number(s?.pending_handoffs) || 0,
    });
  };

  const fetchAttention = useCallback(
    async ({ silent = false } = {}) => {
      if (!accessToken || !orgId) {
        setAttentionItems([]);
        setAttentionTotal(0);
        setAttentionLoading(false);
        return [];
      }
      if (!silent) setAttentionLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/notifications`, {
          headers: authHeaders(),
          params: {
            org_id: orgId,
            undismissed: 1,
            limit: ATTENTION_LIMIT,
            types: ATTENTION_TYPES,
          },
          withCredentials: true,
        });
        const next = Array.isArray(res.data?.items) ? res.data.items : [];
        const count = Number(res.data?.total);
        const total = Number.isFinite(count) ? count : next.length;
        setAttentionItems(next);
        setAttentionTotal(total);
        setAttentionOpen(total > 0);
        try {
          await axios.post(`${API_BASE_URL}/notifications/read-all`, null, {
            headers: authHeaders(),
            params: { org_id: orgId, types: ATTENTION_TYPES },
            withCredentials: true,
          });
        } catch (seenErr) {
          console.error("Failed to mark Attention notifications seen:", seenErr);
        }
        return next;
      } catch (err) {
        console.error("Failed to load My work Attention:", err);
        setAttentionItems([]);
        setAttentionTotal(0);
        setAttentionOpen(false);
        return [];
      } finally {
        if (!silent) setAttentionLoading(false);
      }
    },
    [accessToken, orgId, authHeaders]
  );

  const fetchList = useCallback(
    async ({ offset = 0, append = false } = {}) => {
      if (!accessToken || !orgId) {
        setMyWorkItems([]);
        setMyWorkTotal(0);
        applySummary({});
        setMyWorkLoading(false);
        return;
      }
      if (append) setLoadingMore(true);
      else setMyWorkLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/posts/my-work`, {
          headers: authHeaders(),
          params: {
            org_id: orgId,
            view: "list",
            limit: MY_WORK_PAGE_SIZE,
            offset,
          },
          withCredentials: true,
        });
        const next = Array.isArray(res.data?.items) ? res.data.items : [];
        const count = Number(res.data?.total);
        setMyWorkTotal(Number.isFinite(count) ? count : next.length);
        applySummary(res.data?.summary);
        setMyWorkItems((prev) => (append ? [...prev, ...next] : next));
      } catch (err) {
        console.error("Failed to load My work list:", err);
        if (!append) {
          setMyWorkItems([]);
          setMyWorkTotal(0);
          applySummary({});
        }
      } finally {
        if (append) setLoadingMore(false);
        else setMyWorkLoading(false);
      }
    },
    [accessToken, orgId, authHeaders]
  );

  const fetchUpcoming = useCallback(async () => {
    if (!accessToken || !orgId) {
      setBuckets(EMPTY_BUCKETS);
      setMyWorkTotal(0);
      setUpcomingTruncated(false);
      applySummary({});
      setMyWorkLoading(false);
      return;
    }
    setMyWorkLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/posts/my-work`, {
        headers: authHeaders(),
        params: {
          org_id: orgId,
          view: "upcoming",
        },
        withCredentials: true,
      });
      const b = res.data?.buckets || {};
      setBuckets({
        overdue: Array.isArray(b.overdue) ? b.overdue : [],
        today: Array.isArray(b.today) ? b.today : [],
        this_week: Array.isArray(b.this_week) ? b.this_week : [],
        later: Array.isArray(b.later) ? b.later : [],
        no_date: Array.isArray(b.no_date) ? b.no_date : [],
      });
      const count = Number(res.data?.total);
      setMyWorkTotal(Number.isFinite(count) ? count : 0);
      setUpcomingTruncated(Boolean(res.data?.truncated));
      applySummary(res.data?.summary);
    } catch (err) {
      console.error("Failed to load My work upcoming:", err);
      setBuckets(EMPTY_BUCKETS);
      setMyWorkTotal(0);
      setUpcomingTruncated(false);
      applySummary({});
    } finally {
      setMyWorkLoading(false);
    }
  }, [accessToken, orgId, authHeaders]);

  useEffect(() => {
    fetchAttention();
  }, [fetchAttention]);

  useEffect(() => {
    if (workView === "upcoming" || workView === "roadmap") {
      fetchUpcoming();
    } else {
      fetchList({ offset: 0, append: false });
    }
  }, [workView, fetchList, fetchUpcoming]);

  const setView = (next) => {
    const v =
      next === "upcoming" || next === "roadmap" ? next : "list";
    setWorkView(v);
    writeWorkViewPref(orgId, v);
  };

  const setThisSprintFilter = (on) => {
    setThisSprintOnly(!!on);
    writeThisSprintPref(orgId, !!on);
  };

  const scrollToRef = (ref) => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        ref?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    });
  };

  const jumpToOverdue = () => {
    setView("upcoming");
    scrollToRef(overdueSectionRef);
  };

  const handleWeekDayClick = (day) => {
    if (day?.isToday) {
      scrollToRef(todaySectionRef);
    } else {
      scrollToRef(thisWeekSectionRef);
    }
  };

  const markDismissed = async (id) => {
    await axios.post(`${API_BASE_URL}/notifications/${id}/dismiss`, null, {
      headers: authHeaders(),
      withCredentials: true,
    });
  };

  const markAllDismissed = async () => {
    await axios.post(`${API_BASE_URL}/notifications/dismiss-all`, null, {
      headers: authHeaders(),
      params: { org_id: orgId, types: ATTENTION_TYPES },
      withCredentials: true,
    });
  };

  const removeAttentionLocal = (id) => {
    setAttentionItems((prev) =>
      prev.filter((row) => notificationId(row) !== id)
    );
    setAttentionTotal((prev) => Math.max(0, prev - 1));
  };

  const backfillAttention = async () => {
    await fetchAttention({ silent: true });
  };

  const handleDismiss = async (e, n) => {
    e.preventDefault();
    e.stopPropagation();
    const id = notificationId(n);
    if (!id) return;
    removeAttentionLocal(id);
    try {
      await markDismissed(id);
    } catch (err) {
      console.error("Failed to dismiss Attention item:", err);
    }
    await backfillAttention();
  };

  const handleClearAll = async () => {
    if (!orgId || clearing || attentionItems.length === 0) return;
    setClearing(true);
    const previous = attentionItems;
    const previousTotal = attentionTotal;
    const ids = previous.map(notificationId).filter(Boolean);
    setAttentionItems([]);
    setAttentionTotal((prev) => Math.max(0, prev - ids.length));
    try {
      await markAllDismissed();
      await backfillAttention();
    } catch (err) {
      console.error("Failed to clear Attention:", err);
      setAttentionItems(previous);
      setAttentionTotal(previousTotal);
    } finally {
      setClearing(false);
    }
  };

  const handleAttentionClick = async (n) => {
    const path = getNotificationPath(n);
    if (shouldOpenDmDrawer(n) && path) {
      const payload = n.payload || {};
      const notifOrgId = n.org_id ?? n.orgId ?? payload.org_id ?? orgId;
      const conversationId =
        payload.conversation_id ?? n.entity_id ?? n.entityId;
      if (notifOrgId) setSelectedOrgId(String(notifOrgId));
      const isDesktop =
        typeof window !== "undefined" &&
        window.matchMedia("(min-width: 768px)").matches;
      if (isDesktop && notifOrgId && conversationId && accessToken) {
        try {
          const list = await listDmConversations(accessToken, notifOrgId);
          const conv = list.find(
            (c) => Number(c.conversation_id) === Number(conversationId)
          );
          openConversation(conversationId, { peer: conv?.peer || null });
          return;
        } catch (err) {
          console.error("Failed to open DM drawer from My work:", err);
        }
      }
    }
    if (path) navigate(path);
  };

  const loadInspectTeam = useCallback(
    async (tid) => {
      if (!tid || !accessToken || !orgId) {
        setInspectTeam(EMPTY_INSPECT_TEAM);
        return;
      }
      const cached = inspectTeamCacheRef.current.get(tid);
      if (cached) setInspectTeam(cached);
      try {
        const headers = { Authorization: `Bearer ${accessToken}` };
        const params = { org_id: orgId };
        const [membersRes, lanesRes, sprintsRes, channels, membershipsRes] =
          await Promise.all([
          axios
            .get(`${API_BASE_URL}/team/${tid}/members`, {
              headers,
              params,
              withCredentials: true,
            })
            .catch(() => ({ data: [] })),
          axios
            .get(`${API_BASE_URL}/team/${tid}/kanban-lanes`, {
              headers,
              params,
              withCredentials: true,
            })
            .catch(() => ({ data: [] })),
          axios
            .get(`${API_BASE_URL}/team/${tid}/sprints`, {
              headers,
              params,
              withCredentials: true,
            })
            .catch(() => ({ data: [] })),
          listTeamChannels(accessToken, orgId, tid).catch(() => []),
          axios
            .get(`${API_BASE_URL}/team/memberships`, {
              headers,
              params,
              withCredentials: true,
            })
            .catch(() => ({ data: [] })),
        ]);
        const members = mapTeamMembers(membersRes.data);
        const memberIds = new Set(
          members.map(memberIdOf).filter(Boolean).map(String)
        );
        const uid = authUserIdFromToken(accessToken);
        const fromMemberships = (
          Array.isArray(membershipsRes.data) ? membershipsRes.data : []
        ).some((t) => Number(t.team_id ?? t.teamId) === Number(tid));
        const isMember =
          fromMemberships ||
          (uid != null && memberIds.has(String(uid)));
        const defaultChannel =
          (Array.isArray(channels) ? channels : []).find((ch) => ch.is_default) ||
          null;
        const next = {
          teamId: tid,
          members,
          memberIds,
          lanes: Array.isArray(lanesRes.data) ? lanesRes.data : [],
          sprints: Array.isArray(sprintsRes.data) ? sprintsRes.data : [],
          defaultChannel,
          isMember,
        };
        inspectTeamCacheRef.current.set(tid, next);
        setInspectTeam(next);
      } catch (err) {
        console.error("Failed to load card team for inspect:", err);
        if (!cached) setInspectTeam({ ...EMPTY_INSPECT_TEAM, teamId: tid });
      }
    },
    [accessToken, orgId]
  );

  const handleMyWorkClick = (item) => {
    const post = item?.post;
    const pid = postIdOf(post);
    if (!pid || !orgId || !accessToken) return;
    setInspectCard(post);
    setInspectEditOpen(false);
    const previewTeamId = teamIdOf(post);
    if (previewTeamId && inspectTeamCacheRef.current.has(previewTeamId)) {
      setInspectTeam(inspectTeamCacheRef.current.get(previewTeamId));
    } else if (!previewTeamId) {
      setInspectTeam(EMPTY_INSPECT_TEAM);
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/posts/${pid}`, {
          params: { org_id: orgId },
          headers: { Authorization: `Bearer ${accessToken}` },
          withCredentials: true,
        });
        setInspectCard(data);
        const tid = teamIdOf(data) || teamIdOf(post);
        await loadInspectTeam(tid);
      } catch (err) {
        console.error("Failed to load card for inspect:", err);
        const tid = teamIdOf(post);
        await loadInspectTeam(tid);
      }
    })();
  };

  const handleRoadmapCardClick = (post) => {
    handleMyWorkClick({ kind: "assigned", post });
  };

  const handleRoadmapStubClick = (post) => {
    handleMyWorkClick({ kind: "handoff", post });
  };

  const handleOpenOnBoard = (card) => {
    const pid = postIdOf(card);
    const tid = teamIdOf(card);
    if (!pid || !orgId || !tid) return;
    const href = boardHrefForCard({
      orgId,
      teamId: tid,
      postId: pid,
      sprintId: sprintIdOf(card),
    });
    if (href) navigate(withReturnQuery(href, returnPath));
  };

  const handleOpenInPage = (card) => {
    const pid = postIdOf(card);
    if (!pid || !orgId) return;
    const href = permalinkForPost({ orgId, postId: pid });
    if (href) navigate(withReturnQuery(href, returnPath));
  };

  const handleInspectEdit = (card) => {
    if (!inspectTeam.isMember) return;
    if (card) setInspectCard(card);
    setInspectEditOpen(true);
  };

  const refreshInspectCard = async (card = inspectCard) => {
    const pid = postIdOf(card);
    if (!pid || !accessToken || !orgId) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/posts/${pid}`, {
        params: { org_id: orgId },
        headers: { Authorization: `Bearer ${accessToken}` },
        withCredentials: true,
      });
      setInspectCard(data);
    } catch (err) {
      console.error("Failed to refresh inspected card:", err);
    }
  };

  const refreshMyWork = () => {
    if (workView === "upcoming" || workView === "roadmap") {
      fetchUpcoming();
    } else {
      fetchList({ offset: 0, append: false });
    }
  };

  const teamBoardHref = (teamId, tab = "kanban") => {
    if (!orgId || !teamId) return null;
    const params = new URLSearchParams({
      org_id: String(orgId),
      tab,
    });
    return `/teams/${encodeURIComponent(String(teamId))}?${params.toString()}`;
  };

  const handleShowMore = () => {
    if (loadingMore || myWorkItems.length >= myWorkTotal) return;
    fetchList({ offset: myWorkItems.length, append: true });
  };

  const visibleListItems = useMemo(
    () => (thisSprintOnly ? filterThisSprintItems(myWorkItems) : myWorkItems),
    [myWorkItems, thisSprintOnly]
  );
  const visibleBuckets = useMemo(
    () => (thisSprintOnly ? filterThisSprintBuckets(buckets) : buckets),
    [buckets, thisSprintOnly]
  );
  const roadmapItems = useMemo(() => {
    const flat = flattenUpcomingBuckets(buckets);
    return thisSprintOnly ? filterThisSprintItems(flat) : flat;
  }, [buckets, thisSprintOnly]);
  const roadmapPosts = useMemo(
    () =>
      roadmapItems
        .filter((item) => item?.kind !== "handoff")
        .map((item) => item.post)
        .filter(Boolean),
    [roadmapItems]
  );
  const roadmapStubs = useMemo(
    () =>
      roadmapItems
        .filter((item) => item?.kind === "handoff")
        .map((item) => item.post)
        .filter(Boolean),
    [roadmapItems]
  );
  const teams = useMemo(
    () => groupMyWorkByTeam(visibleListItems),
    [visibleListItems]
  );
  const openChip =
    (summary.open_assigned || 0) + (summary.pending_handoffs || 0);
  const now = Date.now();
  const upcomingItemCount = UPCOMING_BUCKETS.reduce(
    (n, b) => n + (visibleBuckets[b.key]?.length || 0),
    0
  );
  const listEmpty = visibleListItems.length === 0;
  const upcomingEmpty = upcomingItemCount === 0;
  const roadmapEmpty = roadmapItems.length === 0;
  const rawHasWork =
    workView === "list"
      ? myWorkItems.length > 0
      : UPCOMING_BUCKETS.some((b) => (buckets[b.key] || []).length > 0);
  const workLoading =
    myWorkLoading &&
    (workView === "list" ? myWorkItems.length === 0 : !rawHasWork);

  const inspectHierarchy = useMemo(() => {
    if (!inspectCard) return [];
    const parent = inspectCard.parent;
    return parent ? [inspectCard, parent] : [inspectCard];
  }, [inspectCard]);

  if (!orgId) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8 text-sm text-muted-foreground">
        Select a workspace to view My work.
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-full px-4 py-4 md:px-6 md:py-6 space-y-6">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <ListTodo className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold text-foreground">My work</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Personal reminders, open cards assigned to you, and handoffs waiting
          on your teams in {orgName}.
        </p>
      </header>

      {/* Attention */}
      <section className="rounded-lg border border-border bg-background">
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <button
            type="button"
            onClick={() => setAttentionOpen((v) => !v)}
            className="flex min-w-0 flex-1 items-center gap-1.5 text-left rounded-md -ml-1 px-1 py-0.5 hover:bg-muted/50"
            aria-expanded={attentionOpen}
          >
            {attentionOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="text-sm font-semibold text-foreground">
              Attention
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {attentionTotal}
            </span>
          </button>
          {attentionOpen && attentionItems.length > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground"
              disabled={clearing}
              onClick={handleClearAll}
            >
              {clearing ? "Clearing…" : "Clear all"}
            </Button>
          ) : null}
        </div>
        {attentionOpen ? (
          <div className="border-t border-border px-2 pb-2">
            {attentionLoading ? (
              <p className="px-2 py-3 text-sm text-muted-foreground">
                Loading…
              </p>
            ) : attentionItems.length === 0 ? (
              <p className="px-2 py-3 text-sm text-muted-foreground">
                Nothing that needs your attention.
              </p>
            ) : (
              <ul className="space-y-1 pt-1">
                {attentionItems.map((n) => {
                  const id = notificationId(n);
                  const title = getNotificationTitle(n);
                  const body = getNotificationBody(n);
                  return (
                    <li
                      key={id}
                      className="flex items-start gap-1 rounded-md hover:bg-muted/60 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => handleAttentionClick(n)}
                        className="min-w-0 flex-1 text-left px-2 py-1.5"
                      >
                        <p className="text-sm text-foreground leading-snug">
                          {title}
                        </p>
                        {body ? (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {body}
                          </p>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        aria-label="Dismiss"
                        title="Dismiss"
                        onClick={(e) => handleDismiss(e, n)}
                        className="shrink-0 mt-1 mr-1 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : null}
      </section>

      <PersonalTasksSection orgId={orgId} accessToken={accessToken} />

      <div className="flex flex-wrap gap-2">
        <span
          className="inline-flex min-h-10 items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-foreground md:min-h-0 md:py-1 md:px-2.5"
          title="Open assigned cards plus pending inbound handoffs"
        >
          Open
          <span className="font-semibold tabular-nums">{openChip}</span>
        </span>
        <button
          type="button"
          onClick={jumpToOverdue}
          className={cn(
            "inline-flex min-h-10 items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors md:min-h-0 md:py-1 md:px-2.5",
            summary.overdue > 0 ? "text-destructive" : "text-foreground"
          )}
          title="Show overdue items in By due"
        >
          Overdue
          <span className="font-semibold tabular-nums">{summary.overdue}</span>
        </button>
        <span
          className="inline-flex min-h-10 items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-foreground md:min-h-0 md:py-1 md:px-2.5"
          title="Pending inbound handoffs to your teams"
        >
          Handoffs
          <span className="font-semibold tabular-nums">
            {summary.pending_handoffs}
          </span>
        </span>
        <span
          className="inline-flex min-h-10 items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-foreground md:min-h-0 md:py-1 md:px-2.5"
          title="Needs attention until you dismiss (X or Clear all)"
        >
          Attention
          <span className="font-semibold tabular-nums">{attentionTotal}</span>
        </span>
      </div>

      {/* Work: By team | By due | Roadmap */}
      <section className="space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-0.5">
            <h2 className="text-sm font-semibold text-foreground">
              {workView === "upcoming"
                ? "By due"
                : workView === "roadmap"
                  ? "Roadmap"
                  : "By team"}
            </h2>
            <p className="text-xs text-muted-foreground">
              {workView === "upcoming"
                ? "Same work, sorted by due date (including overdue and undated)."
                : workView === "roadmap"
                  ? "Your open cards on a time horizon."
                  : "Grouped by team. Click a card to inspect it."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0 self-start">
            <button
              type="button"
              onClick={() => setThisSprintFilter(!thisSprintOnly)}
              className={cn(
                "min-h-10 rounded-md border px-3 py-1.5 text-xs transition-colors md:min-h-0 md:px-2.5 md:py-1",
                thisSprintOnly
                  ? "border-foreground/30 bg-muted text-foreground font-medium"
                  : "border-border bg-background text-muted-foreground hover:text-foreground"
              )}
              title="Show only cards in each team’s current sprint (inbound handoffs always shown)"
            >
              This sprint
            </button>
            <div
              className="inline-flex rounded-md border border-border p-0.5 bg-background"
              role="tablist"
              aria-label="Work view"
            >
              <button
                type="button"
                role="tab"
                aria-selected={workView === "list"}
                title="Open assigned cards and handoffs, grouped by team"
                onClick={() => setView("list")}
                className={cn(
                  "min-h-10 px-3 py-1.5 text-xs rounded-sm transition-colors md:min-h-0 md:px-2.5 md:py-1",
                  workView === "list"
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                By team
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={workView === "upcoming"}
                title="Same open work, organized by due date (overdue, today, this week, later, no date)"
                onClick={() => setView("upcoming")}
                className={cn(
                  "min-h-10 px-3 py-1.5 text-xs rounded-sm transition-colors md:min-h-0 md:px-2.5 md:py-1",
                  workView === "upcoming"
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                By due
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={workView === "roadmap"}
                title="Timeline of your open cards"
                onClick={() => setView("roadmap")}
                className={cn(
                  "min-h-10 px-3 py-1.5 text-xs rounded-sm transition-colors md:min-h-0 md:px-2.5 md:py-1",
                  workView === "roadmap"
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Roadmap
              </button>
            </div>
          </div>
        </div>

        {workLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : workView === "list" ? (
          listEmpty ? (
            <div className="rounded-lg border border-border bg-background px-4 py-6 text-sm text-muted-foreground space-y-2">
              <p>
                {thisSprintOnly && rawHasWork
                  ? "No assigned cards in current sprints."
                  : "No open assigned cards or inbound handoffs right now. Assigned cards come from teams with a board."}
              </p>
              {thisSprintOnly && rawHasWork ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setThisSprintFilter(false)}
                >
                  Clear This sprint filter
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-4">
              {teams.map((group) => {
                const boardHref = teamBoardHref(group.teamId, "kanban");
                const roadmapHref = teamBoardHref(group.teamId, "roadmap");
                return (
                  <div
                    key={group.teamId || group.name}
                    className="rounded-lg border border-border bg-background overflow-hidden"
                  >
                    <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-muted/30">
                      <h3 className="text-sm font-medium text-foreground truncate min-w-0">
                        {group.name}
                      </h3>
                      <div className="flex items-center gap-2 shrink-0">
                        {boardHref ? (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={() =>
                              navigate(withReturnQuery(boardHref, returnPath))
                            }
                          >
                            Board
                          </button>
                        ) : null}
                        {roadmapHref ? (
                          <button
                            type="button"
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={() =>
                              navigate(withReturnQuery(roadmapHref, returnPath))
                            }
                          >
                            Roadmap
                          </button>
                        ) : null}
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {group.items.length}
                        </span>
                      </div>
                    </div>
                    <ul className="px-2 py-1 space-y-1">
                      {group.items.map((item) => (
                        <MyWorkRow
                          key={myWorkItemKey(item)}
                          item={item}
                          now={now}
                          onClick={handleMyWorkClick}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
              {!thisSprintOnly && myWorkItems.length < myWorkTotal ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs text-muted-foreground"
                  disabled={loadingMore}
                  onClick={handleShowMore}
                >
                  {loadingMore
                    ? "Loading…"
                    : `Show more (${myWorkItems.length} of ${myWorkTotal})`}
                </Button>
              ) : null}
            </div>
          )
        ) : workView === "roadmap" ? (
          roadmapEmpty ? (
            <div className="rounded-lg border border-border bg-background px-4 py-6 text-sm text-muted-foreground space-y-2">
              <p>
                {thisSprintOnly && rawHasWork
                  ? "No assigned cards in current sprints."
                  : "No open assigned cards or inbound handoffs right now. Assigned cards come from teams with a board."}
              </p>
              {thisSprintOnly && rawHasWork ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setThisSprintFilter(false)}
                >
                  Clear This sprint filter
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3">
              <TeamRoadmap
                posts={roadmapPosts}
                stubs={roadmapStubs}
                completedWorkLane={null}
                onCardClick={handleRoadmapCardClick}
                onStubClick={handleRoadmapStubClick}
                labelForPost={myWorkRoadmapLabel}
                orgId={orgId}
                persistScope="mywork"
              />
              {upcomingTruncated && !thisSprintOnly ? (
                <p className="text-xs text-muted-foreground">
                  Showing the first 200 of {myWorkTotal} open items.
                </p>
              ) : null}
            </div>
          )
        ) : upcomingEmpty ? (
          <div className="rounded-lg border border-border bg-background px-4 py-6 text-sm text-muted-foreground space-y-2">
            <p>
              {thisSprintOnly && rawHasWork
                ? "No assigned cards in current sprints."
                : "No open assigned cards or inbound handoffs right now. Assigned cards come from teams with a board."}
            </p>
            {thisSprintOnly && rawHasWork ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setThisSprintFilter(false)}
              >
                Clear This sprint filter
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <MyWorkWeekStrip
              buckets={visibleBuckets}
              overdueCount={(visibleBuckets.overdue || []).length}
              onOverdueClick={() => scrollToRef(overdueSectionRef)}
              onDayClick={handleWeekDayClick}
            />
            {UPCOMING_BUCKETS.map(({ key, label, title }) => {
              const items = visibleBuckets[key] || [];
              if (items.length === 0) return null;
              const sectionRef =
                key === "overdue"
                  ? overdueSectionRef
                  : key === "today"
                    ? todaySectionRef
                    : key === "this_week"
                      ? thisWeekSectionRef
                      : undefined;
              return (
                <div
                  key={key}
                  ref={sectionRef}
                  className="rounded-lg border border-border bg-background overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-muted/30"
                    title={title}
                  >
                    <h3
                      className={cn(
                        "text-sm font-medium truncate",
                        key === "overdue"
                          ? "text-destructive"
                          : "text-foreground"
                      )}
                      title={title}
                    >
                      {label}
                    </h3>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      {items.length}
                    </span>
                  </div>
                  <ul className="px-2 py-1 space-y-1">
                    {items.map((item) => (
                      <MyWorkRow
                        key={myWorkItemKey(item)}
                        item={item}
                        now={now}
                        onClick={handleMyWorkClick}
                        showTeam
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
            {upcomingTruncated && !thisSprintOnly ? (
              <p className="text-xs text-muted-foreground">
                Showing the first 200 of {myWorkTotal} open items.
              </p>
            ) : null}
          </div>
        )}
      </section>

      <CardInspectOverlay
        epic={inspectCard}
        hierarchyCards={inspectHierarchy}
        completedWorkLane={null}
        orgId={orgId}
        focusId={inspectCard ? postIdOf(inspectCard) : null}
        onOpenChange={(open) => {
          if (!open) {
            setInspectCard(null);
            setInspectEditOpen(false);
            setRemoteParentId(null);
          }
        }}
        onCardClick={handleOpenInPage}
        onCardEdit={inspectTeam.isMember ? handleInspectEdit : undefined}
        onOpenOnBoard={handleOpenOnBoard}
        onOpenRemoteParent={(parent) => {
          const id = parent?.post_id || parent?.postId;
          if (id) setRemoteParentId(id);
        }}
        defaultChannel={inspectTeam.defaultChannel}
        teamId={inspectTeam.teamId || teamIdOf(inspectCard) || null}
        teamMemberIds={inspectTeam.memberIds}
        onViewTeamChannel={() => {
          const tid = inspectTeam.teamId || teamIdOf(inspectCard);
          if (!tid || !orgId) return;
          const params = new URLSearchParams({
            org_id: String(orgId),
            tab: "feed",
          });
          navigate(
            withReturnQuery(
              `/teams/${encodeURIComponent(String(tid))}?${params.toString()}`,
              returnPath
            )
          );
        }}
      />
      <CardEditDialog
        open={inspectEditOpen}
        onOpenChange={setInspectEditOpen}
        card={inspectCard}
        onCardChange={setInspectCard}
        orgId={orgId}
        teamId={inspectTeam.teamId}
        accessToken={accessToken}
        kanbanLanes={inspectTeam.lanes}
        sprints={inspectTeam.sprints}
        teamMembers={inspectTeam.members}
        parentCandidates={inspectCard?.parent ? [inspectCard.parent] : []}
        onSaved={async () => {
          setInspectEditOpen(false);
          await refreshInspectCard();
          refreshMyWork();
        }}
      />
      <LinkedParentDialog
        open={Boolean(remoteParentId)}
        parentId={remoteParentId}
        orgId={orgId}
        accessToken={accessToken}
        onClose={() => setRemoteParentId(null)}
      />

    </div>
  );
};

export default MyWorkPage;
